import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityIcon from "@mui/icons-material/Visibility";
import PersonIcon from "@mui/icons-material/Person";
import VerifiedIcon from "@mui/icons-material/Verified";
import FilterListIcon from "@mui/icons-material/FilterList";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import LinkIcon from "@mui/icons-material/Link";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import RefreshIcon from "@mui/icons-material/Refresh";
import SendIcon from "@mui/icons-material/Send";
import {
  Box,
  IconButton,
  Tooltip,
  useTheme,
  Typography,
  Paper,
  Chip,
  Avatar,
  Button,
  alpha,
  LinearProgress,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import React, { useEffect, useState } from "react";
import Swal from "sweetalert2";
import Header from "../../components/Header";
import { tokens } from "../../theme";
import { API_BASE_URL } from "../../config/apiBase";
import {
  fetchConnectSummary,
  openConnectPortal,
  openStripeUrl,
  resendConnectOnboardingLink,
  startConnectOnboarding,
} from "../../api/stripeConnect";

const UserStripe = () => {
  const theme = useTheme();
  const baseColors = tokens(theme.palette.mode);

  const colors = {
    ...baseColors,
    blueAccent: {
      100: "#d9eefb",
      200: "#b3ddf7",
      300: "#8dcbf4",
      400: "#67baf0",
      500: "#22a3f1",
      600: "#1b82c1",
      700: "#146291",
      800: "#0e4160",
      900: "#072030",
    },
    primary: {
      100: "#ffffff",
      200: "#fafafa",
      300: "#f5f5f5",
      400: "#ffffff",
      500: "#ffffff",
      600: "#e6e6e6",
      700: "#cccccc",
      800: "#b3b3b3",
      900: "#999999",
    },
  };

  const token = localStorage.getItem("accessToken");
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    total: 0,
    connected: 0,
    notConnected: 0,
  });
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedSeller, setSelectedSeller] = useState(null);
  const [connectSummary, setConnectSummary] = useState(null);
  const [connectLoading, setConnectLoading] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `${API_BASE_URL}/seller/getSellers`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (!response.ok) {
        throw new Error(`Failed to fetch data: ${response.statusText}`);
      }
      const responseData = await response.json();

      if (!responseData.success || !Array.isArray(responseData.seller)) {
        throw new Error("Invalid response data format");
      }

      const formatDate = (isoDateString) => {
        const date = new Date(isoDateString);
        return date.toLocaleDateString("en-US", {
          year: "numeric",
          month: "short",
          day: "numeric",
        });
      };

      const mappedData = responseData.seller.map((item) => ({
        id: item.id,
        email: item.email,
        firstName: item.firstName,
        lastName: item.lastName,
        contactNumber: item.contactNumber,
        createdAt: formatDate(item.createdAt),
        isVerified: item.isVerified,
        role: item.role,
        stripeaccountID: item.stripeaccountID,
        stripeCustomerId: item.stripeCustomerId,
        stripeEmailID: item.stripeEmailID,
        balance_cents: item.balance_cents,
        bankAccountNumber: item.bankAccountNumber,
        payPal: item.payPal,
        // Create full name for avatar
        fullName: `${item.firstName} ${item.lastName}`,
      }));

      const sortedData = mappedData.sort((a, b) => b.id - a.id);
      setData(sortedData);

      // Calculate statistics
      const totalSellers = sortedData.length;
      const connectedSellers = sortedData.filter(
        (seller) => seller.stripeaccountID
      ).length;

      setStats({
        total: totalSellers,
        connected: connectedSellers,
        notConnected: totalSellers - connectedSellers,
      });
    } catch (error) {
      console.error("Error fetching data:", error);
      Swal.fire("Error!", "Failed to fetch seller data.", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleViewClick = (seller) => {
    setSelectedSeller(seller);
    setConnectSummary(null);
    setViewDialogOpen(true);
  };

  const handleCloseViewDialog = () => {
    setViewDialogOpen(false);
    setSelectedSeller(null);
    setConnectSummary(null);
  };

  const refreshSellerInList = (sellerId, updates) => {
    setData((prev) => {
      const existing = prev.find((row) => row.id === sellerId);
      const wasConnected = !!existing?.stripeaccountID;
      const nowConnected = updates.stripeaccountID
        ? true
        : wasConnected;

      if (!wasConnected && nowConnected) {
        setStats((statsPrev) => ({
          ...statsPrev,
          connected: statsPrev.connected + 1,
          notConnected: Math.max(0, statsPrev.notConnected - 1),
        }));
      }

      return prev.map((row) =>
        row.id === sellerId ? { ...row, ...updates } : row
      );
    });
    setSelectedSeller((prev) =>
      prev && prev.id === sellerId ? { ...prev, ...updates } : prev
    );
  };

  const handleConnectAction = async (action, seller) => {
    if (!seller) return;
    setConnectLoading(true);
    try {
      let result;
      if (action === "onboarding") {
        result = await startConnectOnboarding(seller.id);
        if (result.accountId) {
          refreshSellerInList(seller.id, {
            stripeaccountID: result.accountId,
          });
        }
      } else if (action === "portal") {
        result = await openConnectPortal(seller.id);
      } else if (action === "resend") {
        if (!seller.stripeaccountID) {
          throw new Error("Seller has no Stripe Connect account yet.");
        }
        result = await resendConnectOnboardingLink(
          seller.stripeaccountID,
          seller.id
        );
      } else if (action === "summary") {
        result = await fetchConnectSummary(seller.id);
        setConnectSummary(result);
        return;
      }

      const url = result?.url || result?.onboardingUrl;
      if (url) {
        openStripeUrl(url);
        Swal.fire({
          title: "Stripe opened",
          text:
            action === "portal" && result?.type === "dashboard"
              ? "Stripe Express dashboard opened in a new tab."
              : "Stripe Connect onboarding opened in a new tab. The seller completes KYC and bank details on Stripe.",
          icon: "success",
          background: "#ffffff",
          color: "#333333",
        });
      }
    } catch (error) {
      console.error("Connect action failed:", error);
      Swal.fire("Error", error.message || "Connect action failed.", "error");
    } finally {
      setConnectLoading(false);
    }
  };

  const handleDeleteClick = async (id) => {
    Swal.fire({
      title: "Are you sure?",
      text: "Once deleted, you will not be able to recover this seller!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: colors.redAccent[500],
      cancelButtonColor: colors.grey[500],
      confirmButtonText: "Delete",
      background: "#ffffff",
      color: "#333333",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const response = await fetch(
            `${API_BASE_URL}/seller/deleteSeller/${id}`,
            {
              method: "DELETE",
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          );
          if (!response.ok) {
            throw new Error("Failed to delete seller");
          }

          const updatedData = data.filter((item) => item.id !== id);
          setData(updatedData);

          // Update stats after deletion
          const deletedSeller = data.find((item) => item.id === id);
          setStats((prev) => ({
            total: prev.total - 1,
            connected: deletedSeller.stripeaccountID
              ? prev.connected - 1
              : prev.connected,
            notConnected: !deletedSeller.stripeaccountID
              ? prev.notConnected - 1
              : prev.notConnected,
          }));

          Swal.fire({
            title: "Deleted!",
            text: "The seller has been deleted.",
            icon: "success",
            background: "#ffffff",
            color: "#333333",
          });
        } catch (error) {
          console.error("Error deleting seller:", error);
          Swal.fire("Error!", "Failed to delete seller.", "error");
        }
      }
    });
  };

  // Get initials for avatar
  const getInitials = (name) => {
    return name
      .split(" ")
      .map((word) => word[0])
      .join("")
      .toUpperCase();
  };

  // Get avatar color based on name - using blue shades only
  const getAvatarColor = (name) => {
    const blueShades = [
      "#22a3f1", // Main blue
      "#4db5f5", // Lighter blue
      "#1b82c1", // Darker blue
      "#67baf0", // Even lighter blue
      "#146291", // Even darker blue
    ];

    const charCodeSum = name
      .split("")
      .reduce((sum, char) => sum + char.charCodeAt(0), 0);
    return blueShades[charCodeSum % blueShades.length];
  };

  const columns = [
    { field: "id", headerName: "ID", flex: 0.5 },
    {
      field: "fullName",
      headerName: "Seller",
      flex: 1.8,
      renderCell: (params) => (
        <Box display="flex" alignItems="center">
          <Avatar
            sx={{
              bgcolor: getAvatarColor(params.row.fullName),
              width: 32,
              height: 32,
              mr: 1,
            }}
          >
            {getInitials(params.row.fullName)}
          </Avatar>
          <Box>
            <Typography
              variant="body1"
              fontWeight="bold"
              fontSize="14px"
              color="#333333"
            >
              {`${params.row.firstName} ${params.row.lastName}`}
            </Typography>
          </Box>
        </Box>
      ),
    },
    {
      field: "email",
      headerName: "Email",
      flex: 1.2,
      renderCell: (params) => (
        <Typography color="#333333">{params.row.email}</Typography>
      ),
    },
    {
      field: "contactNumber",
      headerName: "Contact",
      flex: 0.9,
      renderCell: (params) => (
        <Typography color="#333333">{params.row.contactNumber}</Typography>
      ),
    },
    {
      field: "createdAt",
      headerName: "Registration Date",
      flex: 0.9,
      renderCell: (params) => (
        <Typography variant="body2" color="#333333">
          {params.row.createdAt}
        </Typography>
      ),
    },
    {
      field: "stripeaccountID",
      headerName: "Stripe Status",
      flex: 1,
      renderCell: (params) => (
        <Chip
          icon={params.row.stripeaccountID ? <LinkIcon /> : <AccountBalanceIcon />}
          label={params.row.stripeaccountID ? "Connected" : "Not Connected"}
          size="small"
          sx={{
            fontWeight: "bold",
            borderRadius: "4px",
            backgroundColor: params.row.stripeaccountID
              ? alpha("#4CAF50", 0.1)
              : alpha("#757575", 0.1),
            color: params.row.stripeaccountID ? "#2E7D32" : "#616161",
            "& .MuiChip-icon": {
              color: params.row.stripeaccountID ? "#2E7D32" : "#616161",
              fontSize: "14px",
            },
          }}
        />
      ),
    },
    {
      field: "isVerified",
      headerName: "Verified",
      flex: 0.7,
      renderCell: (params) => (
        <Chip
          icon={params.row.isVerified ? <VerifiedIcon /> : <PersonIcon />}
          label={params.row.isVerified ? "Verified" : "Unverified"}
          size="small"
          sx={{
            fontWeight: "bold",
            borderRadius: "4px",
            backgroundColor: params.row.isVerified
              ? alpha("#4CAF50", 0.1)
              : alpha("#757575", 0.1),
            color: params.row.isVerified ? "#2E7D32" : "#616161",
            "& .MuiChip-icon": {
              color: params.row.isVerified ? "#2E7D32" : "#616161",
              fontSize: "14px",
            },
          }}
        />
      ),
    },
    {
      field: "role",
      headerName: "Role",
      flex: 0.6,
      renderCell: (params) => (
        <Chip
          label={params.row.role}
          size="small"
          sx={{
            fontWeight: "bold",
            borderRadius: "4px",
            backgroundColor: alpha(colors.blueAccent[500], 0.2),
            color: colors.blueAccent[500],
          }}
        />
      ),
    },
    {
      field: "Actions",
      headerName: "Actions",
      flex: 0.6,
      renderCell: (params) => (
        <Box>
          <Tooltip title="View Stripe Details">
            <IconButton
              onClick={() => handleViewClick(params.row)}
              sx={{
                backgroundColor: alpha(colors.blueAccent[500], 0.1),
                borderRadius: "4px",
                mr: 1,
                padding: "4px",
                "&:hover": {
                  backgroundColor: alpha(colors.blueAccent[500], 0.2),
                },
              }}
            >
              <VisibilityIcon
                sx={{ color: colors.blueAccent[500], fontSize: "18px" }}
              />
            </IconButton>
          </Tooltip>
          
        </Box>
      ),
    },
  ];

  // Custom toolbar component
  const CustomToolbar = () => {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "6px 16px",
          borderBottom: `1px solid #e0e0e0`,
        }}
      >
        <Box display="flex" alignItems="center">
          <Button
            variant="outlined"
            startIcon={<FilterListIcon />}
            size="small"
            sx={{
              borderColor: alpha(colors.blueAccent[500], 0.5),
              color: colors.blueAccent[500],
              mr: 2,
              "&:hover": {
                borderColor: colors.blueAccent[500],
                backgroundColor: alpha(colors.blueAccent[500], 0.1),
              },
            }}
          >
            Filter
          </Button>
          <GridToolbar
            sx={{
              "& .MuiButton-root": {
                color: "#757575",
                "&:hover": {
                  backgroundColor: alpha("#e0e0e0", 0.5),
                },
              },
            }}
          />
        </Box>
      </Box>
    );
  };

  // View Details Dialog
  const ViewDetailsDialog = ({ open, onClose, seller }) => {
    if (!seller) return null;

    return (
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle sx={{ 
          backgroundColor: colors.blueAccent[500],
          color: "white",
          display: "flex",
          alignItems: "center",
          gap: 1
        }}>
          <AccountBalanceIcon />
          Stripe Account Details
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
            <Avatar
              sx={{
                bgcolor: getAvatarColor(seller.fullName),
                width: 48,
                height: 48,
                mr: 2,
              }}
            >
              {getInitials(seller.fullName)}
            </Avatar>
            <Box>
              <Typography variant="h6" fontWeight="bold">
                {seller.fullName}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {seller.email}
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
            <Box>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Stripe Status
              </Typography>
              <Chip
                icon={seller.stripeaccountID ? <LinkIcon /> : <AccountBalanceIcon />}
                label={seller.stripeaccountID ? "Connected" : "Not Connected"}
                color={seller.stripeaccountID ? "success" : "default"}
                variant="outlined"
              />
            </Box>

            <Box>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Account Verified
              </Typography>
              <Chip
                icon={seller.isVerified ? <VerifiedIcon /> : <PersonIcon />}
                label={seller.isVerified ? "Verified" : "Unverified"}
                color={seller.isVerified ? "success" : "default"}
                variant="outlined"
              />
            </Box>

            {seller.stripeaccountID && (
              <>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Stripe Account ID
                  </Typography>
                  <Typography variant="body2" sx={{ 
                    fontFamily: 'monospace',
                    backgroundColor: alpha(colors.blueAccent[500], 0.1),
                    p: 1,
                    borderRadius: 1,
                    wordBreak: 'break-all'
                  }}>
                    {seller.stripeaccountID}
                  </Typography>
                </Box>

                <Box>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Stripe Customer ID
                  </Typography>
                  <Typography variant="body2" sx={{ 
                    fontFamily: 'monospace',
                    backgroundColor: alpha(colors.blueAccent[500], 0.1),
                    p: 1,
                    borderRadius: 1,
                    wordBreak: 'break-all'
                  }}>
                    {seller.stripeCustomerId || "Not Available"}
                  </Typography>
                </Box>

                {seller.stripeEmailID && (
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Stripe Email
                    </Typography>
                    <Typography variant="body2">
                      {seller.stripeEmailID}
                    </Typography>
                  </Box>
                )}

                {seller.balance_cents !== null && (
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Balance
                    </Typography>
                    <Typography variant="body2" fontWeight="bold">
                      ${(seller.balance_cents / 100).toFixed(2)}
                    </Typography>
                  </Box>
                )}
              </>
            )}

            {!seller.stripeaccountID && (
              <Box sx={{ gridColumn: "1 / -1" }}>
                <Paper
                  sx={{
                    p: 2,
                    backgroundColor: alpha("#ff9800", 0.1),
                    border: `1px solid ${alpha("#ff9800", 0.3)}`,
                  }}
                >
                  <Typography variant="body2" color="text.secondary" align="center">
                    This seller has not connected their Stripe account yet. Use
                    &quot;Start Connect onboarding&quot; to register them on
                    Stripe Express (KYC and bank details are collected on
                    Stripe&apos;s site).
                  </Typography>
                </Paper>
              </Box>
            )}
          </Box>

          <Box sx={{ mt: 3 }}>
            <Typography variant="h6" gutterBottom>
              Stripe Connect actions
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Admin-assisted payout setup. Opens Stripe in a new browser tab;
              the seller completes identity and bank verification on Stripe.
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Button
                variant="contained"
                startIcon={<OpenInNewIcon />}
                disabled={connectLoading || !seller.email}
                onClick={() => handleConnectAction("onboarding", seller)}
                sx={{ backgroundColor: colors.blueAccent[500] }}
              >
                Start Connect onboarding
              </Button>
              <Button
                variant="outlined"
                startIcon={<AccountBalanceIcon />}
                disabled={connectLoading}
                onClick={() => handleConnectAction("portal", seller)}
                sx={{
                  borderColor: colors.blueAccent[500],
                  color: colors.blueAccent[500],
                }}
              >
                Open payout portal
              </Button>
              {seller.stripeaccountID && (
                <>
                  <Button
                    variant="outlined"
                    startIcon={<SendIcon />}
                    disabled={connectLoading}
                    onClick={() => handleConnectAction("resend", seller)}
                    sx={{
                      borderColor: colors.blueAccent[500],
                      color: colors.blueAccent[500],
                    }}
                  >
                    Resend onboarding link
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<RefreshIcon />}
                    disabled={connectLoading}
                    onClick={() => handleConnectAction("summary", seller)}
                    sx={{
                      borderColor: colors.blueAccent[500],
                      color: colors.blueAccent[500],
                    }}
                  >
                    Load account summary
                  </Button>
                </>
              )}
            </Stack>
          </Box>

          {connectSummary && (
            <Box sx={{ mt: 3 }}>
              <Typography variant="h6" gutterBottom>
                Connect account summary
              </Typography>
              <Paper
                variant="outlined"
                sx={{ p: 2, backgroundColor: alpha(colors.blueAccent[500], 0.05) }}
              >
                <Stack spacing={1}>
                  <Typography variant="body2">
                    <strong>Account ID:</strong> {connectSummary.accountId}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Charges enabled:</strong>{" "}
                    {connectSummary.chargesEnabled ? "Yes" : "No"}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Payouts enabled:</strong>{" "}
                    {connectSummary.payoutsEnabled ? "Yes" : "No"}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Details submitted:</strong>{" "}
                    {connectSummary.detailsSubmitted ? "Yes" : "No"}
                  </Typography>
                  {connectSummary.requirements?.currently_due?.length > 0 && (
                    <Typography variant="body2" color="warning.main">
                      <strong>Currently due:</strong>{" "}
                      {connectSummary.requirements.currently_due.join(", ")}
                    </Typography>
                  )}
                </Stack>
              </Paper>
            </Box>
          )}

          {/* Additional Payment Information */}
          {(seller.bankAccountNumber || seller.payPal) && (
            <Box sx={{ mt: 3 }}>
              <Typography variant="h6" gutterBottom>
                Additional Payment Methods
              </Typography>
              <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
                {seller.bankAccountNumber && (
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Bank Account
                    </Typography>
                    <Typography variant="body2">
                      {seller.bankAccountNumber}
                    </Typography>
                  </Box>
                )}
                {seller.payPal && (
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      PayPal
                    </Typography>
                    <Typography variant="body2">
                      {seller.payPal}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} color="primary" disabled={connectLoading}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  return (
    <Box m="20px">
      {/* Header with inline stats */}
      <Box
        mb={2}
        p={2.5}
        borderRadius={2}
        sx={{
          background: `linear-gradient(90deg, #22a3f1 0%, #67baf0 100%)`,
          boxShadow: `0 4px 12px rgba(0, 0, 0, 0.1)`,
          border: `1px solid rgba(255, 255, 255, 0.3)`,
        }}
      >
        <Typography
          variant="h3"
          fontWeight="bold"
          color="white"
          sx={{ mb: 0.5 }}
        >
          Stripe Account Management
        </Typography>

        <Box display="flex" flexDirection="column" gap={1}>
          <Typography variant="body1" color="white">
            Monitor and manage seller Stripe account connections
          </Typography>

          {/* Inline stats */}
          <Stack direction="row" spacing={2} mt={1}>
            <Chip
              label={`Total: ${stats.total}`}
              size="small"
              sx={{
                fontWeight: "bold",
                backgroundColor: "rgba(255, 255, 255, 0.2)",
                color: "white",
                px: 1,
              }}
            />
            <Chip
              icon={<LinkIcon sx={{ fontSize: 14 }} />}
              label={`Connected: ${stats.connected}`}
              size="small"
              sx={{
                fontWeight: "bold",
                backgroundColor: "rgba(76, 175, 80, 0.3)",
                color: "white",
                px: 1,
                "& .MuiChip-icon": {
                  color: "white",
                },
              }}
            />
            <Chip
              icon={<AccountBalanceIcon sx={{ fontSize: 14 }} />}
              label={`Not Connected: ${stats.notConnected}`}
              size="small"
              sx={{
                fontWeight: "bold",
                backgroundColor: "rgba(255, 255, 255, 0.2)",
                color: "white",
                px: 1,
                "& .MuiChip-icon": {
                  color: "white",
                },
              }}
            />
          </Stack>
        </Box>
      </Box>

      <Paper
        elevation={3}
        sx={{
          borderRadius: 2,
          overflow: "hidden",
          backgroundColor: "#ffffff",
          boxShadow: `0 10px 30px rgba(0, 0, 0, 0.05)`,
          border: `1px solid #e0e0e0`,
        }}
      >
        <Box
          sx={{
            height: "55vh",
            minHeight: "450px",
            width: "100%",
            "& .MuiDataGrid-root": {
              border: "none",
              fontSize: "13px",
              color: "#333333",
            },
            "& .MuiDataGrid-cell": {
              borderBottom: `1px solid #e0e0e0`,
              padding: "12px",
              fontSize: "13px",
            },
            "& .MuiDataGrid-columnHeaders": {
              backgroundColor: "#22a3f1",
              borderBottom: "none",
              fontSize: "14px",
              fontWeight: "bold",
              padding: "0 12px",
              color: "white",
            },
            "& .MuiDataGrid-virtualScroller": {
              backgroundColor: "#ffffff",
            },
            "& .MuiDataGrid-footerContainer": {
              borderTop: "none",
              backgroundColor: "rgba(34, 163, 241, 0.05)",
              padding: "6px 12px",
              color: "#333333",
            },
            "& .MuiDataGrid-row": {
              "&:hover": {
                backgroundColor: "rgba(34, 163, 241, 0.1)",
              },
            },
            "& .MuiTablePagination-root": {
              color: "#333333",
            },
            "& .MuiIconButton-root": {
              color: "#22a3f1",
            },
            "& .MuiCheckbox-root": {
              color: "#22a3f1",
            },
            "& .MuiSvgIcon-root": {
              "&.MuiSelect-icon": {
                color: "#757575",
              },
            },
            "& .MuiMenuItem-root": {
              color: "#333333",
            },
          }}
        >
          <DataGrid
            rows={data}
            columns={columns}
            components={{
              Toolbar: CustomToolbar,
              LoadingOverlay: LinearProgress,
            }}
            loading={loading}
            disableSelectionOnClick
            rowHeight={56}
            pageSize={8}
            rowsPerPageOptions={[8, 16, 32]}
            sx={{
              "& .MuiDataGrid-columnSeparator": {
                display: "none",
              },
            }}
          />
        </Box>
      </Paper>

      {/* View Details Dialog */}
      <ViewDetailsDialog
        open={viewDialogOpen}
        onClose={handleCloseViewDialog}
        seller={selectedSeller}
      />
    </Box>
  );
};

export default UserStripe;